import { FC, useEffect } from 'react';

import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';

import AnimateLabel from 'components/AnimateLabel/AnimateLabel';
import CheckBox from 'components/CheckboxGroup/CheckboxGroup';
import { FormError } from 'components/FormError/FormError';
import { FormLabel } from 'components/FormLabel/FormLabel';
import { If } from 'components/Generics';
import { useFieldRefContext } from 'contexts/filesRefs.context';
import { Option } from 'types/select';
import { TeethExtraction } from 'types/TreatmentPlanning.types';
import { translate } from 'utils';

import {
  leftBottomBabyTeethOptions,
  leftBottomTeethOptions,
  leftTopBabyTeethOptions,
  leftTopTeethOptions,
  rightBottomBabyTeethOptions,
  rightBottomTeethOptions,
  rightTopBabyTeethOptions,
  rightTopTeethOptions
} from './ToothChart.config';

const resolveFormPrefix = (chartName: string) =>
  chartName.includes('ZenClearUltra')
    ? 'treatmentSpecifications.ZenClearUltra'
    : 'treatmentSpecifications';

type TeethToExtractProp = {
  isBaby: boolean;
  onChange: (value: typeof initialValue) => void;
  value: typeof initialValue | null;
  isCrowdExtraction?: boolean;
  error?: string;
  name: string;
  title?: string;
  showError?: boolean;
  disabled?: boolean;
  animateLabel?: string;
  source?: string;
  isUpperDisabled?: boolean;
  isLowerDisabled?: boolean;
  isToothChartRestricted?: boolean;
  restrictedToothChartValue?: TeethExtraction;
};
const initialValue = {
  rightTop: [] as Option[],
  rightBottom: [] as Option[],
  leftTop: [] as Option[],
  leftBottom: [] as Option[]
};
const TeethToExtract: FC<TeethToExtractProp> = ({
  isBaby = false,
  onChange,
  value,
  error,
  name,
  title,
  showError = true,
  disabled = false,
  animateLabel = 'treatmentSpecifications.teethToExtract',
  source,
  isUpperDisabled,
  isLowerDisabled,
  isToothChartRestricted,
  isCrowdExtraction,
  restrictedToothChartValue
}) => {
  const { getValues, setValue, watch } = useFormContext();
  const formPrefix = resolveFormPrefix(name);
  const toothInfoChartPath = isBaby
    ? `${formPrefix}.toothInformation.primaryToothChart`
    : `${formPrefix}.toothInformation.toothChart`;
  const toothMovementRestrictionChartPath = isBaby
    ? `${formPrefix}.toothMovementRestriction.primaryToothChart`
    : `${formPrefix}.toothMovementRestriction.toothChart`;
  const watchToothInfo = watch(toothInfoChartPath);
  const watchToothMovementRestriction = watch(
    toothMovementRestrictionChartPath
  );
  const isExtractionChart =
    name.endsWith('.extraction.toothChart') ||
    name.endsWith('.extraction.primaryToothChart');

  const updateToothChartList = (
    TeethOptions: Option[],
    position: 'leftBottom' | 'leftTop' | 'rightBottom' | 'rightTop'
  ) => {
    // disabling the checkBoxes in  in toothMovement,ToothAttachment,and Extraction Tooth Chart list , corresponding to the  values selected in toothInfo
    const { [position]: toothInfoList } = restrictedToothChartValue || {
      leftBottom: [],
      leftTop: [],
      rightBottom: [],
      rightTop: []
    };

    const updatedToothChart = TeethOptions?.map((tooth) => {
      if (toothInfoList?.find((item: Option) => item?.value === tooth?.value)) {
        return {
          ...tooth,
          disabled: true
        };
      }
      return tooth;
    });
    if (isCrowdExtraction) {
      // the crowd extraction tooth chart it is linked to the tooth movement restriction tooth Chart also
      const { [position]: toothMovementRestrictionList } = getValues(
        toothMovementRestrictionChartPath
      ) || { leftBottom: [], leftTop: [], rightBottom: [], rightTop: [] };
      return updatedToothChart?.map((tooth) => {
        if (
          toothMovementRestrictionList?.find(
            (item: Option) => item?.value === tooth?.value
          )
        ) {
          return {
            ...tooth,
            disabled: true
          };
        }
        return tooth;
      });
    }
    return updatedToothChart;
  };

  const modifySelectedToothChartList = (
    selectedList: Option[],
    position: 'leftBottom' | 'leftTop' | 'rightBottom' | 'rightTop'
  ) => {
    //removing the common values in the  selectedList of toothInfo , and  the selected List of toothMovement,ToothAttachment,and Extraction Tooth Chart list
    const { [position]: toothInfoList } = restrictedToothChartValue || {
      leftBottom: [],
      leftTop: [],
      rightBottom: [],
      rightTop: []
    };

    const modifiedSelectedList = selectedList?.filter((tooth) => {
      return !toothInfoList?.find(
        (item: Option) => item?.value === tooth?.value
      );
    });
    if (isCrowdExtraction) {
      //removing the common values in the  selectedList of toothMovementRestriction , and  the selected List of  Extraction Tooth Chart list
      const { [position]: toothMovementRestrictionList } = getValues(
        toothMovementRestrictionChartPath
      ) || { leftBottom: [], leftTop: [], rightBottom: [], rightTop: [] };

      return modifiedSelectedList?.filter((tooth) => {
        return !toothMovementRestrictionList?.find(
          (item: Option) => item?.value === tooth?.value
        );
      });
    }
    return modifiedSelectedList;
  };

  const getOptions = (
    position: 'leftBottom' | 'leftTop' | 'rightBottom' | 'rightTop',
    isBaby: boolean,
    isToothChartRestricted: boolean,
    TeethOptions: Option[],
    babyToothOptions: Option[]
  ) => {
    const options = isBaby ? babyToothOptions : TeethOptions;
    if (isToothChartRestricted) {
      return updateToothChartList(options, position);
    }
    return options;
  };

  const getToothChartListValues = (
    value: typeof initialValue,
    position: 'rightTop' | 'rightBottom' | 'leftTop' | 'leftBottom'
  ) => {
    return isToothChartRestricted
      ? modifySelectedToothChartList(value[position] || [], position)
      : value[position] || [];
  };

  const isPrescriptionSummary = source === 'PrescriptionSummary';
  const checkBoxContainerClassName = `${
    isPrescriptionSummary ? 'md:flex-wrap md:justify-between' : ''
  } md:flex  grid grid-cols-4 gap-x-12 md:gap-0`;
  const { animatedField, callback } = useFieldRefContext();

  const chartValue = value ?? initialValue;
  const rightTopTeeth = getToothChartListValues(chartValue, 'rightTop');
  const rightBottomTeeth = getToothChartListValues(chartValue, 'rightBottom');
  const leftTopTeeth = getToothChartListValues(chartValue, 'leftTop');
  const leftBottomTeeth = getToothChartListValues(chartValue, 'leftBottom');

  useEffect(() => {
    if (isToothChartRestricted && value) {
      setValue(name, {
        rightTop: modifySelectedToothChartList(
          value.rightTop || [],
          'rightTop'
        ),
        rightBottom: modifySelectedToothChartList(
          value.rightBottom || [],
          'rightBottom'
        ),
        leftTop: modifySelectedToothChartList(value.leftTop || [], 'leftTop'),
        leftBottom: modifySelectedToothChartList(
          value.leftBottom || [],
          'leftBottom'
        )
      });
    }
  }, [watchToothInfo, restrictedToothChartValue]);

  useEffect(() => {
    if (isToothChartRestricted && value && isExtractionChart) {
      setValue(name, {
        rightTop: modifySelectedToothChartList(
          value.rightTop || [],
          'rightTop'
        ),
        rightBottom: modifySelectedToothChartList(
          value.rightBottom || [],
          'rightBottom'
        ),
        leftTop: modifySelectedToothChartList(value.leftTop || [], 'leftTop'),
        leftBottom: modifySelectedToothChartList(
          value.leftBottom || [],
          'leftBottom'
        )
      });
    }
  }, [watchToothMovementRestriction]);

  return (
    <div>
      <If condition={!!title}>
        <div
          className={classNames('text-17px mb-6', {
            'text-ERROR': error,
            'text-GRAY3': disabled
          })}
        >
          <label>{title}</label>
        </div>
      </If>
      <div
        className={classNames(
          'flex md:flex-row flex-col flex-wrap  md:rounded-lg border-1 md:border-solid md:border-LIGHT_GRAY_2 overflow-x-auto rounded-lg px-4 py-6 md:w-fit w-auto',
          {
            'bg-LIGHT_ERROR_RED': error,
            'bg-white': !error,
            'md:p-10': !isPrescriptionSummary
          }
        )}
        ref={callback?.(name)}
      >
        <div
          className={classNames('relative mb-10 md:mb-0', {
            'md:w-full': isPrescriptionSummary
          })}
        >
          <h3>
            <FormLabel
              className='text-sm'
              label={translate('prescriptions.right')}
              error={!!error}
              disabled={disabled}
            />
            <AnimateLabel
              animate={animatedField === animateLabel}
              className='text-ERROR md:text-17px text-base font-normal'
            >
              {translate('prescriptions.right')}
            </AnimateLabel>
          </h3>
          <div
            className={classNames({
              'md:w-full': isPrescriptionSummary,
              'md:flex': !isPrescriptionSummary
            })}
          >
            <CheckBox
              onChangehandle={(updatedRightTop) => {
                onChange({
                  ...chartValue,
                  rightTop: updatedRightTop
                });
              }}
              options={getOptions(
                'rightTop',
                isBaby,
                !!isToothChartRestricted,
                rightTopTeethOptions,
                rightTopBabyTeethOptions
              )}
              name='Right Top Teeth'
              selectedItems={rightTopTeeth}
              checkBoxClassName='border-RADIO_BORDER'
              checkBoxContainerClassName={checkBoxContainerClassName}
              labelClassName='flex flex-col-reverse md:items-center md:mr-6'
              checkBoxInnerContainerClassName='w-5'
              disabled={disabled || isUpperDisabled}
            />
          </div>
          <div
            className={classNames({
              'md:w-full': isPrescriptionSummary
            })}
          >
            <CheckBox
              onChangehandle={(updatedRightBottom) => {
                onChange({
                  ...chartValue,
                  rightBottom: updatedRightBottom
                });
              }}
              options={getOptions(
                'rightBottom',
                isBaby,
                !!isToothChartRestricted,
                rightBottomTeethOptions,
                rightBottomBabyTeethOptions
              )}
              name='Right Bottom Teeth'
              selectedItems={rightBottomTeeth}
              checkBoxClassName='border-RADIO_BORDER'
              checkBoxContainerClassName={checkBoxContainerClassName}
              labelClassName='flex flex-col-reverse md:items-center md:mr-6'
              checkBoxInnerContainerClassName='w-5'
              disabled={disabled || isLowerDisabled}
            />
          </div>
        </div>
        <div
          className={classNames('relative', {
            'md:w-full': isPrescriptionSummary
          })}
        >
          <h3>
            <FormLabel
              className='text-sm'
              label={translate('prescriptions.left')}
              error={!!error}
              disabled={disabled}
            />
            <AnimateLabel
              animate={animatedField === animateLabel}
              className='text-ERROR md:text-17px text-base font-normal'
            >
              {translate('prescriptions.left')}
            </AnimateLabel>
          </h3>
          <div
            className={classNames({
              'md:w-full': isPrescriptionSummary,
              'md:flex': !isPrescriptionSummary
            })}
          >
            <CheckBox
              onChangehandle={(updatedLeftTop) => {
                onChange({
                  ...chartValue,
                  leftTop: updatedLeftTop
                });
              }}
              options={getOptions(
                'leftTop',
                isBaby,
                !!isToothChartRestricted,
                leftTopTeethOptions,
                leftTopBabyTeethOptions
              )}
              name='Left Top Teeth'
              selectedItems={leftTopTeeth}
              checkBoxClassName='border-RADIO_BORDER'
              checkBoxContainerClassName={checkBoxContainerClassName}
              labelClassName='flex flex-col-reverse md:items-center md:mr-6'
              checkBoxInnerContainerClassName='w-5'
              disabled={disabled || isUpperDisabled}
            />
          </div>
          <div
            className={classNames({
              'md:w-full': isPrescriptionSummary
            })}
          >
            <CheckBox
              onChangehandle={(updatedLeftBottom) => {
                onChange({
                  ...chartValue,
                  leftBottom: updatedLeftBottom
                });
              }}
              options={getOptions(
                'leftBottom',
                isBaby,
                !!isToothChartRestricted,
                leftBottomTeethOptions,
                leftBottomBabyTeethOptions
              )}
              name='Left Bottom Teeth'
              selectedItems={leftBottomTeeth}
              checkBoxClassName='border-RADIO_BORDER'
              checkBoxContainerClassName={checkBoxContainerClassName}
              labelClassName='flex flex-col-reverse md:items-center md:mr-6'
              checkBoxInnerContainerClassName='w-5'
              disabled={disabled || isLowerDisabled}
            />
          </div>
        </div>
      </div>
      <If condition={showError}>
        <FormError errorMessage={error} />
      </If>
    </div>
  );
};

export default TeethToExtract;
